import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    TrendingUp, CreditCard, Wallet, ArrowUpRight,
    ArrowDownRight, Clock, CheckCircle, XCircle, Loader2,
    X, Eye, Download, Receipt, IndianRupee,
    ChevronDown, ChevronUp, RefreshCw, Banknote, PiggyBank,
    BarChart3, Users
} from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth, API } from '../App';

const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const StatusBadge = ({ status }) => {
    const configs = {
        pending: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
        processing: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Loader2 },
        completed: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
        failed: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
    };
    const cfg = configs[status] || configs.pending;
    const Icon = cfg.icon;
    return (
        <Badge variant="outline" className={cfg.color}>
            <Icon className={`w-3 h-3 mr-1 ${status === 'processing' ? 'animate-spin' : ''}`} />
            {status?.charAt(0).toUpperCase() + status?.slice(1)}
        </Badge>
    );
};

const AdminPayments = () => {
    const { token } = useAuth();
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [payouts, setPayouts] = useState([]);
    const [selectedPayout, setSelectedPayout] = useState(null);
    const [showProcessModal, setShowProcessModal] = useState(false);
    const [showLedgerModal, setShowLedgerModal] = useState(false);
    const [vendorLedger, setVendorLedger] = useState(null);
    const [processForm, setProcessForm] = useState({ status: 'processing', admin_notes: '', payment_reference: '' });
    const [payoutFilter, setPayoutFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [refreshing, setRefreshing] = useState(false);

    const headers = { Authorization: `Bearer ${token}` };

    const fetchOverview = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/admin/payments/overview`, { headers });
            setOverview(res.data);
        } catch (e) { toast.error('Failed to load payment overview'); }
    }, [token]);

    const fetchPayouts = useCallback(async () => {
        try {
            const params = {};
            if (payoutFilter !== 'all') params.status = payoutFilter;
            const res = await axios.get(`${API}/api/admin/payments/payouts`, { headers, params });
            setPayouts(res.data);
        } catch (e) { toast.error('Failed to load payouts'); }
    }, [token, payoutFilter]);



    useEffect(() => {
        const load = async () => {
            setLoading(true);
            await Promise.all([fetchOverview(), fetchPayouts()]);
            setLoading(false);
        };
        if (token) load();
    }, [token, payoutFilter]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await Promise.all([fetchOverview(), fetchPayouts()]);
        setRefreshing(false);
        toast.success('Data refreshed');
    };

    const handleProcessPayout = async () => {
        if (!selectedPayout) return;
        try {
            await axios.put(
                `${API}/api/admin/payments/payouts/${selectedPayout.id}/process`,
                processForm,
                { headers }
            );
            toast.success(`Payout marked as ${processForm.status}`);
            setShowProcessModal(false);
            setSelectedPayout(null);
            setProcessForm({ status: 'processing', admin_notes: '', payment_reference: '' });
            fetchPayouts();
            fetchOverview();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to process payout');
        }
    };



    const handleViewLedger = async (vendorId) => {
        try {
            const res = await axios.get(`${API}/api/admin/payments/vendor/${vendorId}/ledger`, { headers });
            setVendorLedger(res.data);
            setShowLedgerModal(true);
        } catch (e) {
            toast.error('Failed to load vendor ledger');
        }
    };

    const handleExport = async (type) => {
        try {
            const res = await axios.get(`${API}/api/admin/payments/export`, { headers, params: { type } });
            const data = res.data.data;
            if (!data?.length) { toast.info('No data to export'); return; }

            const csvHeaders = Object.keys(data[0]);
            const csv = [csvHeaders.join(','), ...data.map(row => csvHeaders.map(h => {
                const val = row[h];
                if (typeof val === 'object') return JSON.stringify(val);
                return typeof val === 'string' && val.includes(',') ? `"${val}"` : val;
            }).join(','))].join('\n');

            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${type}_export_${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(`${type} data exported`);
        } catch (e) { toast.error('Export failed'); }
    };

    const handleAutoSettle = async () => {
        try {
            const res = await axios.post(`${API}/api/admin/payments/auto-settle`, {}, { headers });
            toast.success(res.data.message);
            fetchOverview();
            fetchPayouts();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to run auto-settlement');
        }
    };

    const tabs = [
        { id: 'overview', label: 'Overview', icon: BarChart3 },
        { id: 'payouts', label: 'Payout Queue', icon: Banknote },
        { id: 'vendors', label: 'Vendor Balances', icon: Users },
    ];

    if (loading) {
        return (
            <DashboardLayout userRole="admin">
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userRole="admin">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                            <CreditCard className="w-8 h-8 text-primary" />
                            Payment Management
                        </h1>
                        <p className="text-muted-foreground mt-1">Manage vendor payouts, margins, and financial reports</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={handleRefresh} disabled={refreshing} className="gap-2">
                            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                            Refresh
                        </Button>
                        <Button variant="outline" onClick={() => handleExport('payouts')} className="gap-2">
                            <Download className="w-4 h-4" />
                            Export Payouts
                        </Button>
                        <Button variant="outline" onClick={() => handleExport('transactions')} className="gap-2">
                            <Download className="w-4 h-4" />
                            Export Transactions
                        </Button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 p-1 bg-muted/50 rounded-xl overflow-x-auto">
                    {tabs.map((tab) => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${isActive
                                    ? 'bg-background text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                                {tab.id === 'payouts' && overview?.payout_counts?.pending > 0 && (
                                    <span className="bg-amber-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                                        {overview.payout_counts.pending}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* OVERVIEW TAB */}
                {activeTab === 'overview' && overview && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                        {/* Stat Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {[
                                { label: 'Total Revenue', value: formatCurrency(overview.total_revenue), icon: IndianRupee, color: 'from-emerald-500 to-green-600', desc: 'From completed orders' },
                                { label: 'Platform Margin', value: formatCurrency(overview.total_platform_margin), icon: PiggyBank, color: 'from-violet-500 to-purple-600', desc: 'From product pricing' },
                                { label: 'Total Payouts', value: formatCurrency(overview.total_payouts_amount), icon: Banknote, color: 'from-blue-500 to-indigo-600', desc: `${overview.payout_counts?.completed || 0} completed` },
                                { label: 'Pending Payouts', value: formatCurrency(overview.pending_payouts_amount), icon: Clock, color: 'from-amber-500 to-orange-600', desc: `${overview.payout_counts?.pending || 0} awaiting` },
                            ].map((stat, i) => (
                                <motion.div
                                    key={stat.label}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                    className="relative overflow-hidden rounded-xl border bg-card"
                                >
                                    <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
                                    <div className="relative p-5">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm font-medium text-muted-foreground">{stat.label}</span>
                                            <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.color}`}>
                                                <stat.icon className="w-4 h-4 text-white" />
                                            </div>
                                        </div>
                                        <p className="text-2xl font-bold">{stat.value}</p>
                                        <p className="text-xs text-muted-foreground mt-1">{stat.desc}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Payout Pipeline */}
                        <div className="bg-card rounded-xl border p-6">
                            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                                <Receipt className="w-5 h-5 text-primary" />
                                Payout Pipeline
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {[
                                    { label: 'Pending', count: overview.payout_counts?.pending, amount: overview.pending_payouts_amount, color: 'border-amber-300 bg-amber-50' },
                                    { label: 'Processing', count: overview.payout_counts?.processing, amount: overview.processing_payouts_amount, color: 'border-blue-300 bg-blue-50' },
                                    { label: 'Completed', count: overview.payout_counts?.completed, amount: overview.total_payouts_amount, color: 'border-green-300 bg-green-50' },
                                    { label: 'Failed', count: overview.payout_counts?.failed, amount: 0, color: 'border-red-300 bg-red-50' },
                                ].map((item) => (
                                    <div key={item.label} className={`rounded-xl border-2 ${item.color} p-4 text-center`}>
                                        <p className="text-2xl font-bold">{item.count || 0}</p>
                                        <p className="text-sm font-medium text-muted-foreground">{item.label}</p>
                                        <p className="text-xs font-mono mt-1">{formatCurrency(item.amount)}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Top Vendors Quick View */}
                        {overview.vendor_summaries?.length > 0 && (
                            <div className="bg-card rounded-xl border p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-semibold text-lg flex items-center gap-2">
                                        <Users className="w-5 h-5 text-primary" />
                                        Top Vendors by Revenue
                                    </h3>
                                    <Button variant="ghost" size="sm" onClick={() => setActiveTab('vendors')}>
                                        View All <ArrowUpRight className="w-3 h-3 ml-1" />
                                    </Button>
                                </div>
                                <div className="space-y-3">
                                    {overview.vendor_summaries.slice(0, 5).map((v, i) => (
                                        <div key={v.vendor_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                                                    {i + 1}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-sm">{v.vendor_name}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {formatCurrency(v.total_sales)} total sales
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-semibold text-sm text-green-600">{formatCurrency(v.vendor_earnings)}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {formatCurrency(v.available_balance)} available
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </motion.div>
                )}

                {/* PAYOUTS TAB */}
                {activeTab === 'payouts' && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                        {/* Filters */}
                        <div className="flex flex-wrap gap-2">
                            {['all', 'pending', 'processing', 'completed', 'failed'].map((f) => (
                                <Button
                                    key={f}
                                    variant={payoutFilter === f ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setPayoutFilter(f)}
                                    className="capitalize"
                                >
                                    {f}
                                    {f === 'pending' && overview?.payout_counts?.pending > 0 && (
                                        <span className="ml-1 bg-white/20 px-1.5 py-0.5 rounded text-xs">{overview.payout_counts.pending}</span>
                                    )}
                                </Button>
                            ))}
                        </div>

                        {/* Payout Table */}
                        {payouts.length === 0 ? (
                            <div className="bg-card rounded-xl border p-12 text-center">
                                <Banknote className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                                <h3 className="text-lg font-medium text-muted-foreground">No Payouts Found</h3>
                                <p className="text-sm text-muted-foreground/70 mt-1">
                                    {payoutFilter !== 'all' ? `No ${payoutFilter} payouts` : 'No payout requests yet'}
                                </p>
                            </div>
                        ) : (
                            <div className="bg-card rounded-xl border overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-muted/50">
                                            <tr>
                                                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Vendor</th>
                                                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Payout Amount</th>
                                                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Requested</th>
                                                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Status</th>
                                                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">UTR / Reference</th>
                                                <th className="text-right text-xs font-medium text-muted-foreground uppercase px-4 py-3">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {payouts.map((payout) => (
                                                <tr key={payout.id} className="hover:bg-muted/30 transition-colors">
                                                    <td className="px-4 py-3">
                                                        <p className="font-medium text-sm">{payout.vendor_name}</p>
                                                        <p className="text-xs text-muted-foreground font-mono">{String(payout.vendor_id).slice(-8)}</p>
                                                    </td>
                                                    <td className="px-4 py-3 font-semibold text-sm text-green-600 font-mono">{formatCurrency(payout.amount || payout.net_amount)}</td>
                                                    <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(payout.requested_at)}</td>
                                                    <td className="px-4 py-3"><StatusBadge status={payout.status} /></td>
                                                    <td className="px-4 py-3 text-sm font-mono text-muted-foreground">{payout.payment_reference || '—'}</td>
                                                    <td className="px-4 py-3 text-right">
                                                        <div className="flex items-center justify-end gap-1">
                                                            {payout.status === 'pending' && (
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setSelectedPayout(payout);
                                                                        setProcessForm({ status: 'processing', admin_notes: '', payment_reference: '' });
                                                                        setShowProcessModal(true);
                                                                    }}
                                                                    className="gap-1 text-xs"
                                                                >
                                                                    Process
                                                                </Button>
                                                            )}
                                                            {payout.status === 'processing' && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="gap-1 text-xs border-green-300 text-green-700 hover:bg-green-50"
                                                                    onClick={() => {
                                                                        setSelectedPayout(payout);
                                                                        setProcessForm({ status: 'completed', admin_notes: '', payment_reference: '' });
                                                                        setShowProcessModal(true);
                                                                    }}
                                                                >
                                                                    <CheckCircle className="w-3 h-3" />
                                                                    Complete
                                                                </Button>
                                                            )}
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => handleViewLedger(payout.vendor_id)}
                                                            >
                                                                <Eye className="w-3.5 h-3.5" />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </motion.div>
                )}

                {/* VENDOR BALANCES TAB */}
                {activeTab === 'vendors' && overview && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-lg text-muted-foreground">Vendor Balances</h3>
                            <Button onClick={handleAutoSettle} className="gap-2 bg-violet-600 hover:bg-violet-700 text-white">
                                <TrendingUp className="w-4 h-4" />
                                Run Auto-Settle
                            </Button>
                        </div>
                        {overview.vendor_summaries?.length === 0 ? (
                            <div className="bg-card rounded-xl border p-12 text-center">
                                <Users className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                                <h3 className="text-lg font-medium text-muted-foreground">No Vendors</h3>
                                <p className="text-sm text-muted-foreground/70 mt-1">No approved vendors found</p>
                            </div>
                        ) : (
                            <div className="bg-card rounded-xl border overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-muted/50">
                                            <tr>
                                                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Vendor</th>
                                                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Sales / Earned</th>
                                                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">In Hold</th>
                                                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Settled / Available</th>
                                                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Already Paid</th>
                                                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Auto-Settle Queue</th>
                                                <th className="text-right text-xs font-medium text-muted-foreground uppercase px-4 py-3">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {overview.vendor_summaries.map((v) => (
                                                <tr key={v.vendor_id} className="hover:bg-muted/30 transition-colors">
                                                    <td className="px-4 py-3">
                                                        <p className="font-medium text-sm">{v.vendor_name}</p>
                                                        <p className="text-xs text-muted-foreground font-mono">{String(v.vendor_id).slice(-8)}</p>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <p className="text-sm font-medium">{formatCurrency(v.total_sales)}</p>
                                                        <p className="text-xs text-green-600 font-mono">{formatCurrency(v.vendor_earnings)}</p>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="text-sm font-mono text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                                                            {formatCurrency(v.holding_amount)}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <p className="text-sm font-mono text-foreground">{formatCurrency(v.settled_amount)}</p>
                                                        <p className={`text-xs font-mono font-semibold ${v.available_balance > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                                                            {formatCurrency(v.available_balance)} Avail
                                                        </p>
                                                    </td>
                                                    <td className="px-4 py-3 font-mono text-sm text-blue-600">{formatCurrency(v.total_paid_out)}</td>
                                                    <td className="px-4 py-3">
                                                        {v.auto_settle_amount > 0 ? (
                                                            <span className="flex items-center gap-1 text-xs font-medium text-violet-600 bg-violet-50 px-2 py-1 rounded border border-violet-200">
                                                                <Clock className="w-3 h-3" />
                                                                {formatCurrency(v.auto_settle_amount)}
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs text-muted-foreground">—</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        <Button size="sm" variant="outline" onClick={() => handleViewLedger(v.vendor_id)} className="gap-1">
                                                            <Eye className="w-3.5 h-3.5" />
                                                            Ledger
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </motion.div>
                )}


            </div>

            {/* Process Payout Modal */}
            <AnimatePresence>
                {showProcessModal && selectedPayout && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                        onClick={() => setShowProcessModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-card rounded-xl border shadow-2xl w-full max-w-lg"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-6 border-b flex items-center justify-between">
                                <h2 className="text-lg font-semibold">Process Payout</h2>
                                <Button variant="ghost" size="icon" onClick={() => setShowProcessModal(false)}>
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                            <div className="p-6 space-y-4">
                                {/* Payout Summary */}
                                <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Vendor</span>
                                        <span className="font-medium">{selectedPayout.vendor_name}</span>
                                    </div>
                                    <div className="border-t pt-2 flex justify-between text-sm font-semibold">
                                        <span>Payout Amount</span>
                                        <span className="text-green-600 font-mono">{formatCurrency(selectedPayout.amount || selectedPayout.net_amount)}</span>
                                    </div>
                                </div>

                                {/* Bank Details */}
                                {selectedPayout.bank_details && (
                                    <div className="bg-blue-50 rounded-lg p-4 space-y-1">
                                        <p className="text-xs font-semibold text-blue-700 uppercase mb-2">Bank Details</p>
                                        <p className="text-sm"><span className="text-blue-600">Bank:</span> {selectedPayout.bank_details.bank_name}</p>
                                        <p className="text-sm"><span className="text-blue-600">A/C:</span> {selectedPayout.bank_details.account_number}</p>
                                        <p className="text-sm"><span className="text-blue-600">IFSC:</span> {selectedPayout.bank_details.ifsc_code}</p>
                                        <p className="text-sm"><span className="text-blue-600">Holder:</span> {selectedPayout.bank_details.account_holder_name}</p>
                                    </div>
                                )}

                                {/* Status */}
                                <div className="space-y-2">
                                    <Label>Action</Label>
                                    <div className="flex gap-2">
                                        {['processing', 'completed', 'failed'].map((s) => (
                                            <Button
                                                key={s}
                                                variant={processForm.status === s ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setProcessForm({ ...processForm, status: s })}
                                                className={`capitalize ${s === 'completed' ? 'border-green-300' : s === 'failed' ? 'border-red-300' : ''}`}
                                            >
                                                {s}
                                            </Button>
                                        ))}
                                    </div>
                                </div>

                                {/* Payment Reference */}
                                <div className="space-y-2">
                                    <Label>Payment Reference / UTR Number</Label>
                                    <Input
                                        placeholder="e.g. UTR202402180001"
                                        value={processForm.payment_reference}
                                        onChange={(e) => setProcessForm({ ...processForm, payment_reference: e.target.value })}
                                    />
                                </div>

                                {/* Admin Notes */}
                                <div className="space-y-2">
                                    <Label>Admin Notes (optional)</Label>
                                    <Input
                                        placeholder="Add a note..."
                                        value={processForm.admin_notes}
                                        onChange={(e) => setProcessForm({ ...processForm, admin_notes: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="p-6 border-t flex justify-end gap-2">
                                <Button variant="outline" onClick={() => setShowProcessModal(false)}>Cancel</Button>
                                <Button onClick={handleProcessPayout} className="gap-2">
                                    <CheckCircle className="w-4 h-4" />
                                    Confirm {processForm.status}
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Vendor Ledger Modal */}
            <AnimatePresence>
                {showLedgerModal && vendorLedger && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                        onClick={() => setShowLedgerModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-card rounded-xl border shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-6 border-b flex items-center justify-between flex-shrink-0">
                                <div>
                                    <h2 className="text-lg font-semibold">{vendorLedger.vendor.business_name}</h2>
                                    <p className="text-sm text-muted-foreground">{vendorLedger.vendor.email}</p>
                                </div>
                                <Button variant="ghost" size="icon" onClick={() => setShowLedgerModal(false)}>
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                            <div className="p-6 overflow-y-auto flex-1 space-y-6">
                                {/* Summary Cards */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {[
                                        { label: 'Total Sales', value: formatCurrency(vendorLedger.summary.completed_sell_amount), color: 'text-foreground' },
                                        { label: 'Platform Margin', value: formatCurrency(vendorLedger.summary.platform_margin), color: 'text-violet-600' },
                                        { label: 'Vendor Earnings', value: formatCurrency(vendorLedger.summary.vendor_earnings), color: 'text-green-600' },
                                        { label: 'Available', value: formatCurrency(vendorLedger.summary.available_balance), color: 'text-primary' },
                                    ].map((s) => (
                                        <div key={s.label} className="bg-muted/30 rounded-lg p-3 text-center">
                                            <p className="text-xs text-muted-foreground">{s.label}</p>
                                            <p className={`text-lg font-bold font-mono ${s.color}`}>{s.value}</p>
                                        </div>
                                    ))}
                                </div>

                                {/* Transactions */}
                                <div>
                                    <h4 className="font-medium text-sm mb-2">Earnings ({vendorLedger.transactions.length})</h4>
                                    <div className="space-y-1 max-h-48 overflow-y-auto">
                                        {vendorLedger.transactions.length === 0 ? (
                                            <p className="text-sm text-muted-foreground text-center py-4">No completed orders yet</p>
                                        ) : vendorLedger.transactions.map((t) => (
                                            <div key={t.order_id} className="flex items-center justify-between p-2 bg-green-50 rounded text-sm">
                                                <div className="flex items-center gap-2">
                                                    <ArrowDownRight className="w-3.5 h-3.5 text-green-600" />
                                                    <span className="font-mono text-xs">{String(t.order_id).slice(-8).toUpperCase()}</span>
                                                    <span className="text-muted-foreground text-xs">{formatDate(t.date)}</span>
                                                </div>
                                                <div className="text-right">
                                                    <span className="font-mono text-green-600 font-medium">{formatCurrency(t.vendor_earning)}</span>
                                                    <span className="text-violet-500 text-xs ml-2">(margin: {formatCurrency(t.platform_margin)})</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Payouts */}
                                <div>
                                    <h4 className="font-medium text-sm mb-2">Payouts ({vendorLedger.payouts.length})</h4>
                                    <div className="space-y-1 max-h-48 overflow-y-auto">
                                        {vendorLedger.payouts.length === 0 ? (
                                            <p className="text-sm text-muted-foreground text-center py-4">No payouts yet</p>
                                        ) : vendorLedger.payouts.map((p) => (
                                            <div key={p.payout_id} className="flex items-center justify-between p-2 bg-blue-50 rounded text-sm">
                                                <div className="flex items-center gap-2">
                                                    <ArrowUpRight className="w-3.5 h-3.5 text-blue-600" />
                                                    <StatusBadge status={p.status} />
                                                    <span className="text-muted-foreground text-xs">{formatDate(p.date)}</span>
                                                </div>
                                                <span className="font-mono text-blue-600 font-medium">{formatCurrency(p.amount)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Bank Details */}
                                {vendorLedger.vendor.bank_details && (
                                    <div className="bg-muted/30 rounded-lg p-4">
                                        <h4 className="font-medium text-sm mb-2">Bank Details</h4>
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                            <div><span className="text-muted-foreground">Bank:</span> {vendorLedger.vendor.bank_details.bank_name}</div>
                                            <div><span className="text-muted-foreground">IFSC:</span> {vendorLedger.vendor.bank_details.ifsc_code}</div>
                                            <div><span className="text-muted-foreground">A/C:</span> ****{String(vendorLedger.vendor.bank_details.account_number || '').slice(-4)}</div>
                                            <div><span className="text-muted-foreground">Holder:</span> {vendorLedger.vendor.bank_details.account_holder_name}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </DashboardLayout>
    );
};

export default AdminPayments;
